import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import PolicyDashboard from "./PolicyDashboard";
import MonthPicker from "../MonthPicker/index.js";

import {
  createReimbursementPath,
  updateReimbursementPath,
  deleteReimbursementPath,
  getReimbursementsPath,
  getReimbursementsSummaryPath,
  getReimbursementsActivityPath,
  getAdjustmentsEmployeesPath,
} from "../../../../services/config";
import { config as axiosConfig } from "../../../../services/configHeader";

import { fetchBranchData } from "../../../../redux/slices/branchSlice";
import { fetchReimburseData } from "../../../../redux/slices/reimbursementSlice";
import { fetchData } from "../../../../redux/slices/shiftsSlice";
import {
  commonSaveData,
  commonGetData,
  commonGetDataTwo,
  commonGetDataFourth,
  initCommonState,
} from "../../../../redux/slices/commonSlice";

import { HasPermission } from "../../../../services/hasPermission.js";
import { getInitials } from "../../../../services/commonServices.js";
import {
  getPolicyGroups, getDefaultPolicyLimits, getPolicyChangeHistory,
} from '../../payrollDB';

// ─── Color tokens — use CSS variables so light/dark theme works ──────────────
const C = {
  bg:              "var(--pay-bg)",
  surface:         "var(--pay-surface)",
  surfaceSecondary:"var(--pay-surface-2)",
  border:          "var(--pay-border)",
  text:            "var(--pay-text)",
  muted:           "var(--pay-text-muted)",
  purple:          "#8660F8",
};

// Default palette used for small breakdown charts and fallbacks
const DEFAULT_COLORS = ["#8660F8", "#F59E0B", "#4ADE80", "#3B82F6", "#A855F7", "#EC4899"];

const pickArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const candidates = [
    payload.data,
    payload.rows,
    payload.records,
    payload.list,
    payload.claims,
    payload.reimbursements,
    payload.reimbursementClaims,
    payload.results,
    payload.items,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    if (candidate && typeof candidate === "object") {
      const nested = pickArray(candidate);
      if (nested.length) return nested;
    }
  }
  return [];
};

const mapReimbursementStatus = (row) => {
  const raw = row.status ?? row.approvalStatus ?? row.approveStatus ?? row.approvedStatus;
  const normalized = String(raw ?? "Pending").toLowerCase();
  if (["1", "approved", "approve"].includes(normalized)) return "Approved";
  if (["2", "rejected", "reject"].includes(normalized)) return "Rejected";
  if (["review", "need review", "needs review"].includes(normalized)) return "Review";
  return "Pending";
};

const normalizeClaimRow = (row, index) => ({
  ...row,
  id: row.id ?? row.reimbursementId ?? row.reimbursement_id ?? row.claimId ?? row.claim_id ?? `${row.staffId || row.staff_id || "claim"}-${index}`,
  staffId: row.staffId ?? row.staff_id,
  staffName: row.staffName ?? row.staff_name ?? row.employeeName ?? row.employee_name ?? row.name,
  employeeId: row.employeeId ?? row.employee_id ?? row.employeeCode ?? row.employee_code,
  department: row.department ?? row.departmentName ?? row.department_name ?? row.dept,
  branch: row.branch ?? row.branchId ?? row.branch_id ?? row.branchName ?? row.branch_name,
  shiftId: row.shiftId ?? row.shift_id,
  category: row.category ?? row.categoryType ?? row.category_type ?? row.reimbursementCategory ?? row.reimbursement_category ?? row.expenseType ?? row.expense_type ?? row.reimbursementType ?? row.type ?? "Other",
  expenseType: row.expenseType ?? row.expense_type ?? row.reimbursementType ?? row.type ?? "Other",
  amount: row.amount ?? row.reimbursementAmount ?? row.reimbursement_amount ?? row.claimAmount ?? row.claim_amount ?? 0,
  submittedDate: row.submittedDate ?? row.submitted_date ?? row.createdAt ?? row.created_at,
  paymentDate: row.paymentDate ?? row.payment_date ?? row.expenseDate ?? row.expense_date,
  policyLabel: row.policyLabel ?? row.policy_label ?? row.policyName ?? row.policy_name ?? row.assignedPolicy ?? row.assigned_policy ?? row.policy?.name ?? row.policy?.label,
  policyKey: row.policyKey ?? row.policy_key ?? row.policy?.key ?? row.assignedPolicyKey ?? row.assigned_policy_key,
  policyGroupKey: row.policyGroupKey ?? row.policy_group_key ?? row.policy?.groupKey ?? row.policy?.group_key ?? row.assignedPolicyGroupKey ?? row.assigned_policy_group_key,
  policyName: row.policyName ?? row.policy_name ?? row.policy?.name,
  policyCheck: (() => {
    const raw = row.policyCheck ?? row.policy_check ?? row.policyStatus ?? row.policy_status ?? row.policy?.status ?? row.policy?.check;
    const normalized = typeof raw === "string" ? raw.toLowerCase() : raw;
    if (normalized) return normalized;
    if (row.policyLabel || row.policyName || row.assignedPolicy || row.assigned_policy || row.policy?.name || row.policy?.label) {
      return "within";
    }
    return undefined;
  })(),
  status: mapReimbursementStatus(row),
});

const normalizeSummary = (payload) => {
  const d = payload?.data && !Array.isArray(payload.data) ? payload.data : payload || {};
  return {
    totalClaims: Number(d.totalClaims ?? d.total_claims ?? d.claimsCount ?? d.count ?? 0),
    pendingApprovals: Number(d.pendingApprovals ?? d.pendingApproval ?? d.pendingClaims ?? d.pending_count ?? 0),
    approvedAmount: Number(d.approvedAmount ?? d.approved_amount ?? d.totalApprovedAmount ?? 0),
    rejectedCount: Number(d.rejectedCount ?? d.rejected_count ?? d.rejectedClaims ?? 0),
    totalAmount: Number(d.totalAmount ?? d.total_amount ?? d.claimAmount ?? 0),
  };
};

// ─── Inline style helpers ─────────────────────────────────────────────────────
const s = {
  input: {
    background: C.surfaceSecondary,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "8px 12px",
    color: C.text,
    fontSize: 12,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  select: {
    background: C.surfaceSecondary,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "8px 12px",
    color: C.text,
    fontSize: 12,
    outline: "none",
    appearance: "none",
    cursor: "pointer",
    paddingRight: 28,
  },
  btnPrimary: {
    background: C.purple,
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    color: "#fff",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    boxShadow: "0 0 20px rgba(134,96,248,0.25)",
  },
  btnOutline: {
    background: C.surfaceSecondary,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "8px 16px",
    color: C.text,
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  tabBtn: (active) => ({
    padding: "6px 16px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    border: active ? `1px solid rgba(134,96,248,0.2)` : "1px solid transparent",
    background: active ? "rgba(134,96,248,0.1)" : "transparent",
    color: active ? C.purple : C.muted,
  }),
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    Pending:  { bg: "rgba(245,158,11,0.1)",  color: "#f59e0b", border: "rgba(245,158,11,0.2)",  dot: "#f59e0b" },
    Approved: { bg: "rgba(34,197,94,0.1)",   color: "#22c55e", border: "rgba(34,197,94,0.2)",   dot: "#22c55e" },
    Rejected: { bg: "rgba(239,68,68,0.1)",   color: "#ef4444", border: "rgba(239,68,68,0.2)",   dot: "#ef4444" },
    Review:   { bg: "rgba(59,130,246,0.1)",  color: "#3b82f6", border: "rgba(59,130,246,0.2)",  dot: "#3b82f6" },
    Draft:    { bg: "rgba(169,163,201,0.1)", color: C.muted,   border: "rgba(169,163,201,0.15)", dot: C.muted  },
  };
  const t = map[status] || map.Draft;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 500, background: t.bg, color: t.color, border: `1px solid ${t.border}`, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.dot, display: "inline-block", flexShrink: 0 }} />
      {status}
    </span>
  );
};

// ─── Policy Check Badge ───────────────────────────────────────────────────────
const PolicyBadge = ({ check, label, onClick }) => {
  if (!check) return <span style={{ color: C.muted, fontSize: 12 }}>—</span>;
  const map = {
    within:   { bg: "rgba(34,197,94,0.1)",  color: "#22c55e", border: "rgba(34,197,94,0.2)",  icon: "fa-circle-check",   text: label ? label : "Within Limit"  },
    near:     { bg: "rgba(245,158,11,0.1)", color: "#f59e0b", border: "rgba(245,158,11,0.2)", icon: "fa-triangle-exclamation", text: label || "Near Limit" },
    exceeded: { bg: "rgba(239,68,68,0.1)",  color: "#ef4444", border: "rgba(239,68,68,0.2)",  icon: "fa-circle-xmark",   text: label || "Exceeded"   },
  };
  const key = typeof check === "string" ? check.toLowerCase() : check;
  const t = map[key];
  if (!t) return null;
  return (
    <span onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 500, background: t.bg, color: t.color, border: `1px solid ${t.border}`, cursor: onClick ? "pointer" : "default", whiteSpace: "nowrap" }}>
      <i className={`fa-solid ${t.icon}`} style={{ fontSize: 10 }} />
      {t.text}
    </span>
  );
};

// ─── Expense Type Cell ────────────────────────────────────────────────────────
const ExpenseTypeCell = ({ type }) => {
  const map = {
    Travel: { icon: "fa-plane",    color: C.purple    },
    Food:   { icon: "fa-utensils", color: "#22c55e"  },
    Fuel:   { icon: "fa-gas-pump", color: "#3b82f6"  },
    Other:  { icon: "fa-briefcase",color: "#a855f7"  },
  };
  const t = map[type] || map.Other;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <i className={`fa-solid ${t.icon}`} style={{ color: t.color, fontSize: 12 }} />
      <span style={{ color: C.text }}>{type || "Other"}</span>
    </div>
  );
};

// ─── Toggle Switch ────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange }) => (
  <div onClick={onChange} style={{ width: 44, height: 24, borderRadius: 12, background: checked ? C.purple : C.surfaceSecondary, border: `1px solid ${checked ? C.purple : C.border}`, position: "relative", cursor: "pointer", transition: "all 0.2s", flexShrink: 0 }}>
    <div style={{ position: "absolute", top: 3, left: checked ? 22 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
  </div>
);

// ─── Category Limit Card ──────────────────────────────────────────────────────
const CategoryCard = ({ icon, label, iconBg, data, onChange }) => (
  <div style={{ background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{icon}</div>
        <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{label}</span>
      </div>
      <Toggle checked={data.enabled} onChange={() => onChange("enabled", !data.enabled)} />
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {[
        { key: "monthly",   label: "Monthly Limit",         prefix: "₹", placeholder: ""          },
        { key: "perDay",    label: "Per Day Cap (Optional)", prefix: "",  placeholder: "No limit"  },
        { key: "maxClaims", label: "Max Claims/Month",       prefix: "",  placeholder: "Unlimited" },
      ].map(({ key, label: lbl, prefix, placeholder }) => (
        <div key={key}>
          <label style={{ fontSize: 10, color: C.muted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>{lbl}</label>
          <div style={{ position: "relative" }}>
            {prefix && <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: C.text, fontWeight: 600 }}>{prefix}</span>}
            <input value={data[key] || ""} onChange={e => onChange(key, e.target.value)} placeholder={placeholder}
              style={{ ...s.input, paddingLeft: prefix ? 22 : 12, fontSize: 14 }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── Policy Limits Drawer ─────────────────────────────────────────────────────
const PolicyLimitsDrawer = ({ open, employee, onClose }) => {
  const [activeGroup, setActiveGroup] = useState("Engineers");
  const [policyActive, setPolicyActive] = useState(true);
  const [monthlyLimit, setMonthlyLimit] = useState(getDefaultPolicyLimits().monthlyLimit);
  const [categories, setCategories] = useState(getDefaultPolicyLimits().categories);

  const catConfig = [
    { key: "Travel", icon: "✈", iconBg: "rgba(59,130,246,0.1)"  },
    { key: "Food",   icon: "🍴", iconBg: "rgba(34,197,94,0.1)"  },
    { key: "Fuel",   icon: "⛽", iconBg: "rgba(245,158,11,0.1)" },
    { key: "Other",  icon: "💼", iconBg: "rgba(168,85,247,0.1)" },
  ];

  const groups = getPolicyGroups();

  const history = getPolicyChangeHistory();

  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 9998, backdropFilter: "blur(4px)" }} />
      <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 9999, width: "min(95vw, 1200px)", background: C.bg, borderLeft: `1px solid ${C.border}`, display: "flex", flexDirection: "column", overflowY: "auto", boxShadow: "-20px 0 60px rgba(0,0,0,0.8)", animation: "slideInRight 0.28s cubic-bezier(0.22,1,0.36,1) both" }}>
        <style>{`@keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

        {/* Header */}
        <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "20px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: "0 0 4px" }}>
              <i className="fa-solid fa-shield-halved" style={{ marginRight: 10, color: C.purple }} />
              Policy Limits
            </h1>
            <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>
              {employee ? `Policy for: ${employee.staffName || employee.employeeId}` : "Manage monthly caps, category limits, and exceptions"}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`, borderRadius: 10, width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ display: "grid", gridTemplateColumns: "260px 1fr 300px", gap: 20, padding: 24 }}>
          {/* Left panel */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: C.muted, fontSize: 14 }}>🔍</span>
              <input placeholder="Search policies..." style={{ ...s.input, paddingLeft: 40, background: C.surfaceSecondary, borderRadius: 12 }} />
            </div>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 14 }}>Policy Groups</div>
              {groups.map(g => {
                const isActive = activeGroup === g.label;
                return (
                  <button key={g.label} onClick={() => setActiveGroup(g.label)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "10px 14px", borderRadius: 10, border: isActive ? `1px solid ${C.purple}` : "1px solid transparent", background: isActive ? "rgba(134,96,248,0.12)" : "transparent", cursor: "pointer", marginBottom: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span>{g.icon}</span>
                      <span style={{ fontSize: 13, color: g.muted ? C.muted : C.text }}>{g.label}</span>
                    </div>
                    <span style={{ fontSize: 11, color: isActive ? C.purple : C.muted }}>{g.count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Center panel */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Policy Information */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 20 }}>ℹ️ Policy Information</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div><label style={{ fontSize: 12, color: C.muted }}>Policy Name</label><input type="text" defaultValue="Engineer Standard Policy" style={{ ...s.input, marginTop: 6 }} /></div>
                <div><label style={{ fontSize: 12, color: C.muted }}>Applies To</label><select style={{ ...s.select, width: "100%", marginTop: 6 }}><option>Designation = Engineer</option></select></div>
                <div><label style={{ fontSize: 12, color: C.muted }}>Branch</label><select style={{ ...s.select, width: "100%", marginTop: 6 }}><option>All Branches</option></select></div>
                <div><label style={{ fontSize: 12, color: C.muted }}>Effective From</label><input type="date" defaultValue="2026-04-01" style={{ ...s.input, marginTop: 6 }} /></div>
              </div>
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div><div style={{ fontWeight: 600, fontSize: 14 }}>Policy Status</div><div style={{ fontSize: 12, color: C.muted }}>Toggle to activate</div></div>
                <Toggle checked={policyActive} onChange={() => setPolicyActive(!policyActive)} />
              </div>
            </div>

            {/* Monthly Cap */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 20 }}>📅 Monthly Employee Cap</h3>
              <label style={{ fontSize: 12, color: C.muted }}>Monthly Limit</label>
              <div style={{ position: "relative", marginTop: 6 }}>
                <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }}>₹</span>
                <input value={monthlyLimit} onChange={e => setMonthlyLimit(e.target.value)} style={{ ...s.input, paddingLeft: 32, borderColor: C.purple }} />
                <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: C.muted }}>per month</span>
              </div>
            </div>

            {/* Category Limits */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 20 }}>🗂️ Category Limits</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {catConfig.map(cat => (
                  <CategoryCard key={cat.key} icon={cat.icon} label={cat.key} iconBg={cat.iconBg}
                    data={categories[cat.key]}
                    onChange={(field, val) => setCategories(prev => ({ ...prev, [cat.key]: { ...prev[cat.key], [field]: val } }))}
                  />
                ))}
              </div>
            </div>

            {/* Change History */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 16 }}>🕐 Change History</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {history.map((h, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.purple, marginTop: 5, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: C.text }}>{h.action}</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{h.date} · {h.user}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right panel */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 16 }}>👁️ Policy Preview</h3>
              <div style={{ background: C.surfaceSecondary, borderRadius: 12, padding: 14, textAlign: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: C.muted }}>Monthly Max</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: C.purple }}>₹{monthlyLimit}</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {catConfig.map(cat => (
                  <div key={cat.key} style={{ background: C.surfaceSecondary, borderRadius: 10, padding: 10, textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: C.muted }}>{cat.key}</div>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>₹{categories[cat.key].monthly}</div>
                  </div>
                ))}
              </div>
            </div>
            <button style={{ ...s.btnPrimary, width: "100%", padding: "14px", borderRadius: 12, justifyContent: "center" }}>💾 Save Changes</button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

// ─── Approval Popover ─────────────────────────────────────────────────────────
const ApprovalPopover = ({ open, anchorRect, amount, onConfirm, onClose }) => {
  const [showPartial, setShowPartial] = useState(false);
  const [partialAmt, setPartialAmt] = useState("");
  useEffect(() => { if (!open) { setShowPartial(false); setPartialAmt(""); } }, [open]);
  if (!open) return null;
  const top  = anchorRect ? Math.max(10, anchorRect.top - 200) : 100;
  const left = anchorRect ? Math.max(10, anchorRect.left - 260) : 100;
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
      <div style={{ position: "fixed", top, left, zIndex: 50, background: C.surfaceSecondary, border: `1px solid rgba(134,96,248,0.3)`, borderRadius: 12, padding: 16, minWidth: 260, boxShadow: "0 10px 40px rgba(0,0,0,0.5)" }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4 }}>Approve Full Amount?</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.purple }}>₹{Number(amount).toLocaleString("en-IN")}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button onClick={() => { onConfirm(amount); onClose(); }} style={{ ...s.btnPrimary, background: "#22c55e", justifyContent: "center", width: "100%" }}>Confirm Approve</button>
          {!showPartial && (
            <button onClick={() => { setShowPartial(true); setPartialAmt(String(amount)); }} style={{ ...s.btnOutline, justifyContent: "center", width: "100%" }}>Approve Partial</button>
          )}
          <button onClick={onClose} style={{ ...s.btnOutline, justifyContent: "center", width: "100%", color: C.muted }}>Cancel</button>
        </div>
        {showPartial && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
            <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 6 }}>Approved Amount</label>
            <input type="number" value={partialAmt} onChange={e => setPartialAmt(e.target.value)} style={{ ...s.input, marginBottom: 8 }} placeholder="Enter amount" />
            <button onClick={() => { onConfirm(partialAmt); onClose(); }} style={{ ...s.btnPrimary, background: "#22c55e", justifyContent: "center", width: "100%" }}>Confirm Partial</button>
          </div>
        )}
      </div>
    </>
  );
};

// ─── Bulk Approve Modal ───────────────────────────────────────────────────────
const BulkModal = ({ open, count, totalAmount, onConfirm, onClose }) => {
  const [note, setNote]   = useState("");
  const [skip, setSkip]   = useState(false);
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 24 }}>
      <div style={{ background: C.surface, border: `1px solid rgba(134,96,248,0.3)`, borderRadius: 14, padding: 24, maxWidth: 420, width: "100%", boxShadow: "0 0 20px rgba(134,96,248,0.15)" }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 16 }}>Approve {count} Claims?</h3>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>Total Amount</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: C.purple }}>₹{Number(totalAmount).toLocaleString("en-IN")}</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={skip} onChange={e => setSkip(e.target.checked)} style={{ width: 16, height: 16 }} />
            <span style={{ fontSize: 13, color: C.text }}>Skip claims exceeding policy</span>
          </label>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 8 }}>Approval Note (optional)</label>
          <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note..." style={{ ...s.input, resize: "none" }} />
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={() => { onConfirm(note, skip); onClose(); }} style={{ ...s.btnPrimary, flex: 1, justifyContent: "center" }}>Confirm Bulk Approve</button>
          <button onClick={onClose} style={{ ...s.btnOutline, flex: 1, justifyContent: "center" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ─── Add Reimbursement Dialog ─────────────────────────────────────────────────
const EXPENSE_CATEGORIES = [
  { key: "Travel",  icon: "fa-plane"        },
  { key: "Food",    icon: "fa-utensils"     },
  { key: "Fuel",    icon: "fa-gas-pump"     },
  { key: "Medical", icon: "fa-kit-medical"  },
  { key: "Other",   icon: "fa-ellipsis"     },
];

const AddReimburseDialog = ({ open, employees, reimburseStaff, onChange, onSave, onClose, isSaving }) => {
  const [receiptFile, setReceiptFile] = useState(null);
  const fileInputRef = useRef(null);

  const selectedEmp = (employees || []).find(e => String(e.staffId) === String(reimburseStaff.staffId));
  const initials = selectedEmp
    ? (selectedEmp.name || "").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)
    : "?";

  const dlgField = {
    width: "100%",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 10,
    padding: "10px 14px",
    color: "#fff",
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
  };

  const sectionLabel = {
    fontSize: 10,
    fontWeight: 600,
    color: "rgba(255,255,255,0.4)",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: 6,
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.7)",
          zIndex: 1200,
          backdropFilter: "blur(4px)",
        }}
      />

      {/* Modal */}
      <div style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        marginTop: "30px",
        zIndex: 1300,
        width: "min(95vw, 440px)",
        maxHeight: "calc(88vh - 60px)",
        overflowY: "auto",
        background: "linear-gradient(160deg, #1c1248 0%, #130e38 100%)",
        border: "1px solid rgba(134,96,248,0.3)",
        borderRadius: 18,
        padding: "18px 20px 20px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.8)",
        fontFamily: "'Outfit', sans-serif",
        color: "#fff",
        boxSizing: "border-box",
      }}>
        {/* ── Header ── */}
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Add Reimbursement</h2>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.08)", border: "none",
              borderRadius: 7, width: 26, height: 26,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "rgba(255,255,255,0.5)", cursor: "pointer", fontSize: 13,
            }}
          >✕</button>
        </div>

        {/* ── Employee selector ── */}
        <div style={{
          background: "rgba(255,255,255,0.05)",
          borderRadius: 12,
          padding: "8px 12px",
          marginBottom: 12,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%",
            background: "#8660F8",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0,
          }}>
            {initials}
          </div>
          <div style={{ flex: 1, position: "relative" }}>
            <select
              name="staffId"
              value={reimburseStaff.staffId}
              onChange={onChange}
              style={{
                width: "100%",
                background: "transparent", border: "none",
                padding: "4px 24px 4px 0",
                appearance: "none", cursor: "pointer",
                color: selectedEmp ? "#fff" : "rgba(255,255,255,0.4)",
                fontSize: 13, outline: "none", fontFamily: "inherit",
              }}
            >
              <option value="" style={{ background: "#1c1248" }}>Select Employee</option>
              {(employees || []).map(emp => (
                <option key={emp.staffId} value={emp.staffId} style={{ background: "#1c1248" }}>
                  {emp.name} ({emp.id})
                </option>
              ))}
            </select>
            <i
              className="fa-solid fa-chevron-down"
              style={{
                position: "absolute", right: 2, top: "50%",
                transform: "translateY(-50%)",
                fontSize: 9, color: "rgba(255,255,255,0.3)", pointerEvents: "none",
              }}
            />
          </div>
        </div>

        {/* ── Expense Title ── */}
        <div style={{ marginBottom: 12 }}>
          <div style={sectionLabel}>Expense Title</div>
          <div style={{ position: "relative" }}>
            <i
              className="fa-solid fa-bars-staggered"
              style={{
                position: "absolute", left: 12, top: "50%",
                transform: "translateY(-50%)",
                color: "rgba(255,255,255,0.3)", fontSize: 12, pointerEvents: "none",
              }}
            />
            <input
              name="expenseTitle"
              value={reimburseStaff.expenseTitle || ""}
              onChange={onChange}
              placeholder="e.g. Client Meeting Dinner"
              style={{ ...dlgField, paddingLeft: 34 }}
            />
          </div>
        </div>

        {/* ── Category chips ── */}
        <div style={{ marginBottom: 12 }}>
          <div style={sectionLabel}>Category</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {EXPENSE_CATEGORIES.map(cat => {
              const active = reimburseStaff.expenseType === cat.key;
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => onChange({ target: { name: "expenseType", value: cat.key } })}
                  style={{
                    display: "flex", alignItems: "center", gap: 5,
                    padding: "6px 12px", borderRadius: 999,
                    border: active ? "1.5px solid #8660F8" : "1px solid rgba(255,255,255,0.15)",
                    background: active ? "rgba(134,96,248,0.22)" : "rgba(255,255,255,0.05)",
                    color: active ? "#c4b5fd" : "rgba(255,255,255,0.6)",
                    fontSize: 12, fontWeight: active ? 600 : 400,
                    cursor: "pointer",
                  }}
                >
                  <i className={`fa-solid ${cat.icon}`} style={{ fontSize: 10 }} />
                  {cat.key}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Amount + Date ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          <div>
            <div style={sectionLabel}>Amount (₹)</div>
            <div style={{ position: "relative" }}>
              <span style={{
                position: "absolute", left: 12, top: "50%",
                transform: "translateY(-50%)",
                color: "rgba(255,255,255,0.35)", fontSize: 13,
              }}>₹</span>
              <input
                name="amount"
                type="number"
                value={reimburseStaff.amount}
                onChange={onChange}
                placeholder="0"
                style={{ ...dlgField, paddingLeft: 26 }}
              />
            </div>
          </div>
          <div>
            <div style={sectionLabel}>Expense Date</div>
            <div style={{ position: "relative" }}>
              <i
                className="fa-regular fa-calendar"
                style={{
                  position: "absolute", left: 12, top: "50%",
                  transform: "translateY(-50%)",
                  color: "rgba(255,255,255,0.3)", fontSize: 12, pointerEvents: "none",
                }}
              />
              <input
                name="paymentDate"
                type="date"
                value={reimburseStaff.paymentDate}
                onChange={onChange}
                style={{ ...dlgField, paddingLeft: 34, colorScheme: "dark" }}
              />
            </div>
          </div>
        </div>

        {/* ── Description ── */}
        <div style={{ marginBottom: 12 }}>
          <div style={sectionLabel}>Description</div>
          <div style={{ position: "relative" }}>
            <textarea
              name="notes"
              rows={2}
              value={reimburseStaff.notes}
              onChange={onChange}
              placeholder="Briefly describe the expense..."
              maxLength={200}
              style={{ ...dlgField, resize: "none", lineHeight: 1.5, paddingBottom: 20 }}
            />
            <span style={{
              position: "absolute", bottom: 6, right: 10,
              fontSize: 10, color: "rgba(255,255,255,0.25)",
            }}>
              {(reimburseStaff.notes || "").length}/200
            </span>
          </div>
        </div>

        {/* ── Receipt / Attachment ── */}
        <div style={{ marginBottom: 16 }}>
          <div style={sectionLabel}>Receipt / Attachment</div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            style={{ display: "none" }}
            onChange={e => setReceiptFile(e.target.files?.[0] || null)}
          />
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1.5px dashed rgba(134,96,248,0.35)",
              borderRadius: 12,
              padding: "10px 14px",
              display: "flex", alignItems: "center", gap: 12,
              cursor: "pointer",
            }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "rgba(255,255,255,0.06)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: 16, color: "rgba(134,96,248,0.7)" }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {receiptFile ? receiptFile.name : "Upload Receipt"}
              </div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>
                {receiptFile ? `${(receiptFile.size / 1024).toFixed(1)} KB` : "PDF, JPG or PNG (Max 5MB)"}
              </div>
            </div>
            <button
              type="button"
              onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}
              style={{
                background: "rgba(134,96,248,0.2)",
                border: "1px solid rgba(134,96,248,0.4)",
                borderRadius: 8,
                padding: "5px 12px",
                color: "#c4b5fd",
                fontSize: 12, fontWeight: 600,
                cursor: "pointer", flexShrink: 0,
              }}
            >
              {receiptFile ? "Change" : "Browse"}
            </button>
          </div>
          {receiptFile && (
            <button
              type="button"
              onClick={() => setReceiptFile(null)}
              style={{
                marginTop: 6, background: "transparent", border: "none",
                color: "rgba(239,68,68,0.7)", fontSize: 11,
                cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
              }}
            >
              <i className="fa-solid fa-xmark" style={{ fontSize: 10 }} /> Remove file
            </button>
          )}
        </div>

        {/* ── Submit ── */}
        <button
          type="button"
          onClick={onSave}
          disabled={isSaving}
          style={{
            width: "100%", padding: "12px",
            background: isSaving
              ? "rgba(134,96,248,0.45)"
              : "linear-gradient(135deg, #8660F8 0%, #6d3ff5 100%)",
            border: "none", borderRadius: 14,
            color: "#fff", fontSize: 14, fontWeight: 700,
            cursor: isSaving ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            boxShadow: isSaving ? "none" : "0 4px 20px rgba(134,96,248,0.4)",
          }}
        >
          {isSaving
            ? <><i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 12 }} /> Submitting…</>
            : <><i className="fa-solid fa-paper-plane" style={{ fontSize: 12 }} /> Submit Request</>
          }
        </button>
      </div>
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PAYROLL_ADJUSTMENTS_HANDOFF_KEY = "payrollAdjustmentsHandoff";

const Reimbursement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user     = JSON.parse(localStorage.getItem("user_data")) || {};

  // ── Redux selectors ──────────────────────────────────────────────────────
  const branchState = useSelector((st) => st.branch);
  const shiftState  = useSelector((st) => st.shift);
  const commonStore = useSelector((st) => st.common);
  const reimburseStore = useSelector((st) => st.reimburse || {});

  // ── UI state ─────────────────────────────────────────────────────────────
  const [adjustmentsHandoff, setAdjustmentsHandoff] = useState(null);
  const [showPolicyDashboard, setShowPolicyDashboard] = useState(false);
  const [policyDashboardEmployee, setPolicyDashboardEmployee] = useState(null);

  // ── Data state ───────────────────────────────────────────────────────────
  const [reimbursementList, setReimbursementList] = useState([]);
  const [employees,         setEmployees]         = useState([]);
  const [summary,           setSummary]           = useState({ totalClaims: 0, pendingApprovals: 0, approvedAmount: 0, rejectedCount: 0, totalAmount: 0 });
  const [recentActivity,    setRecentActivity]    = useState([]);
  const [activityLoading,   setActivityLoading]   = useState(false);
  const [isSaving,          setIsSaving]          = useState(false);

  // ── Filter / pagination state ─────────────────────────────────────────────
  const todayRM        = new Date();
  const defaultMonth   = `${todayRM.getFullYear()}-${String(todayRM.getMonth() + 1).padStart(2, "0")}`;
  const [filterState, setFilterState] = useState({ payrollMonth: defaultMonth, branch: "", shiftId: "", search: "", status: "All", type: "All" });
  const [activeTab,    setActiveTab]  = useState("All");
  const [currentPage,  setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  // ── Selection state ───────────────────────────────────────────────────────
  const [selectedRows, setSelectedRows] = useState([]);
  const [policyDrawer, setPolicyDrawer] = useState({ open: false, employee: null });
  const [popover,      setPopover]      = useState({ open: false, amount: 0, anchorRect: null, rowId: null, rowData: null });
  const [bulkModal,    setBulkModal]    = useState(false);

  // ── Dialog state ──────────────────────────────────────────────────────────
  const [addDialogOpen,  setAddDialogOpen]  = useState(false);
  const [reimburseStaff, setReimburseStaff] = useState({ userId: user.user_id, staffId: "", expenseTitle: "", expenseType: "", amount: "", paymentDate: "", notes: "" });

  // ── API fetch helpers ─────────────────────────────────────────────────────
  const fetchList = useCallback((month) => {
    if (!month) return;
    dispatch(commonGetData(`${getReimbursementsPath}?userId=${user.user_id}&selectedMonth=${month}`));
  }, [dispatch, user.user_id]);

  const fetchSummary = useCallback((month) => {
    if (!month) return;
    dispatch(commonGetDataTwo(`${getReimbursementsSummaryPath}?userId=${user.user_id}&selectedMonth=${month}`));
  }, [dispatch, user.user_id]);

  // ── Fetch Recent Activity ────────────────────────────────────────────────
  const fetchRecentActivity = useCallback(async (month) => {
    if (!user.user_id) return;
    setActivityLoading(true);
    try {
      const params = new URLSearchParams({ userId: String(user.user_id), limit: "8" });
      if (month) params.set("selectedMonth", month);
      params.set("_nc", String(Date.now()));
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URI}${getReimbursementsActivityPath}?${params.toString()}`,
        {
          ...axiosConfig,
          headers: {
            ...(axiosConfig.headers || {}),
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
            Expires: "0",
          },
        }
      );
      const body = res.data || {};
      setRecentActivity(Array.isArray(body.data) ? body.data : []);
    } catch (err) {
      console.error("Failed to load reimbursement activity", err);
      setRecentActivity([]);
    } finally {
      setActivityLoading(false);
    }
  }, [user.user_id]);

  const fetchEmployees = useCallback(() => {
    dispatch(commonGetDataFourth(`${getAdjustmentsEmployeesPath}?userId=${user.user_id}`));
  }, [dispatch, user.user_id]);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchBranchData(user.user_id));
    dispatch(fetchData(user.user_id));
    fetchEmployees();

    const raw = sessionStorage.getItem(PAYROLL_ADJUSTMENTS_HANDOFF_KEY);
    if (raw) {
      sessionStorage.removeItem(PAYROLL_ADJUSTMENTS_HANDOFF_KEY);
      try {
        const handoff = JSON.parse(raw);
        setAdjustmentsHandoff(handoff);
        if (handoff.selectedMonth) {
          setFilterState(p => ({ ...p, payrollMonth: handoff.selectedMonth }));
          const count = handoff.adjustments?.length || 0;
          toast.info(`Loaded ${count} adjustment${count === 1 ? "" : "s"} from Incentives & Advances (${handoff.monthLabel || handoff.selectedMonth})`);
        }
      } catch { /* ignore */ }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Re-fetch when month changes ───────────────────────────────────────────
  useEffect(() => {
    if (filterState.payrollMonth) {
      fetchList(filterState.payrollMonth);
      fetchSummary(filterState.payrollMonth);
      fetchRecentActivity(filterState.payrollMonth);
    }
    if (user.user_id) dispatch(fetchReimburseData({ userId: user.user_id, selectedMonth: filterState.payrollMonth }));
  }, [filterState.payrollMonth, fetchList, fetchSummary, fetchRecentActivity]);

  // ── Handle GET list response ──────────────────────────────────────────────
  useEffect(() => {
    if (commonStore.commonGetSuccess && commonStore.commonGetData) {
      setReimbursementList(pickArray(commonStore.commonGetData).map(normalizeClaimRow));
    }
  }, [commonStore.commonGetSuccess, commonStore.commonGetData]);

  // ── Handle GET summary response ───────────────────────────────────────────
  useEffect(() => {
    if (commonStore.commonGetTwoSuccess && commonStore.commonGetTwoData) {
      setSummary(normalizeSummary(commonStore.commonGetTwoData));
    }
  }, [commonStore.commonGetTwoSuccess, commonStore.commonGetTwoData]);

  // ── Handle GET employees response ────────────────────────────────────────
  useEffect(() => {
    if (commonStore.commonGetFourthSuccess && commonStore.commonGetFourthData) {
      setEmployees(pickArray(commonStore.commonGetFourthData));
    }
  }, [commonStore.commonGetFourthSuccess, commonStore.commonGetFourthData]);

  // ── Handle POST / PUT / DELETE response ──────────────────────────────────
  useEffect(() => {
    if (commonStore.commonSuccess) {
      toast.success(commonStore.commonMessage || "Saved successfully");
      setIsSaving(false);
      setAddDialogOpen(false);
      setReimburseStaff({ userId: user.user_id, staffId: "", expenseTitle: "", expenseType: "", amount: "", paymentDate: "", notes: "" });
      dispatch(initCommonState());
      fetchList(filterState.payrollMonth);
      fetchSummary(filterState.payrollMonth);
      fetchRecentActivity(filterState.payrollMonth);
    }
    if (commonStore.commonError) {
      toast.error(commonStore.commonMessage || "Operation failed");
      setIsSaving(false);
      dispatch(initCommonState());
    }
  }, [commonStore.commonSuccess, commonStore.commonError]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filtered display list ─────────────────────────────────────────────────
  const displayList = useMemo(() => {
    return (Array.isArray(reimbursementList) ? reimbursementList : []).filter(r => {
      const status = r.status || "Pending";
      const matchTab    = activeTab === "All" || status === activeTab;
      const matchStatus = filterState.status === "All" || !filterState.status || status === filterState.status;
      const matchSearch = !filterState.search ||
        (r.staffName || "").toLowerCase().includes(filterState.search.toLowerCase()) ||
        (r.employeeId || "").toLowerCase().includes(filterState.search.toLowerCase());
      const matchBranch = !filterState.branch || String(r.branch || "").includes(String(filterState.branch));
      const matchShift  = !filterState.shiftId || String(r.shiftId || "").includes(String(filterState.shiftId));
      const matchType   = filterState.type === "All" || !filterState.type ||
        (r.expenseType || "").toLowerCase() === filterState.type.toLowerCase();
      return matchTab && matchStatus && matchSearch && matchBranch && matchShift && matchType;
    });
  }, [reimbursementList, activeTab, filterState]);

  // ── Selection helpers ─────────────────────────────────────────────────────
  const toggleSelect    = (id) => setSelectedRows(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedRows(selectedRows.length === displayList.length ? [] : displayList.map(r => r.id));
  const isAllSelected   = displayList.length > 0 && selectedRows.length === displayList.length;
  const selectedCount   = selectedRows.length;
  const selectedTotal   = displayList.filter(r => selectedRows.includes(r.id)).reduce((s, r) => s + Number(r.amount || 0), 0);

  // ── Live reimbursements summary ──────────────────────────────────────────
  const rawLive = reimburseStore.reimburseGetData || {};
  const liveSummary = normalizeSummary(rawLive || summary);
  const pendingCountLive = Number(liveSummary.pendingApprovals ?? summary.pendingApprovals ?? 0);
  const readyToApproveLive = Number(rawLive.readyToApprove ?? Math.max(0, pendingCountLive - 2));
  const needReviewLive = Number(rawLive.needReview ?? 2);

  const policyMatrixRoles = (rawLive.policyMatrix?.roles || rawLive.policyMatrix || null) ? (
    (rawLive.policyMatrix.roles || rawLive.policyMatrix)
  ) : [
    { role: "Executive", cap: "₹3,000" },
    { role: "Engineer", cap: "₹5,000" },
    { role: "Manager", cap: "₹8,000" },
    { role: "Senior Manager", cap: "₹12,000" },
  ];

  const categoryLimitsLive = rawLive.categoryLimits || {
    Travel: "₹2,500/month",
    Food: "₹1,500/month",
    Fuel: "₹3,000/month",
    Other: "₹1,000/month",
  };

  const policyAlertsLive = rawLive.policyAlerts || {
    nearMonthlyLimit: rawLive.nearMonthlyLimit ?? 7,
    exceededCategoryCap: rawLive.exceededCategoryCap ?? 3,
    approvalExceptions: rawLive.approvalExceptions ?? 2,
  };

  const categoryBreakdownLive = Array.isArray(rawLive.categoryBreakdown)
    ? rawLive.categoryBreakdown.map((c) => ({ label: c.label || c.name, amount: c.amount || c.value || "—", pct: Number(c.pct || c.percent || 0), color: c.color }))
    : [
        { label: "Travel", amount: "₹52k", pct: 35, color: C.purple },
        { label: "Food", amount: "₹24k", pct: 16, color: "#22c55e" },
        { label: "Fuel", amount: "₹18k", pct: 12, color: "#3b82f6" },
        { label: "Other", amount: "₹11k", pct: 7, color: "#a855f7" },
      ];

  const approvalAlertsLive = rawLive.approvalAlerts || [
    { label: "Pending > 3 Days", count: rawLive.pendingOver3Days ?? 5, icon: "fa-clock", color: "#ef4444", bg: "rgba(239,68,68,0.05)", border: "rgba(239,68,68,0.2)" },
    { label: "Needs Receipt Fix", count: rawLive.needsReceiptFix ?? 3, icon: "fa-receipt", color: "#f59e0b", bg: "rgba(245,158,11,0.05)", border: "rgba(245,158,11,0.2)" },
    { label: "Duplicate Suspected", count: rawLive.duplicateSuspected ?? 1, icon: "fa-copy", color: "#3b82f6", bg: "rgba(59,130,246,0.05)", border: "rgba(59,130,246,0.2)" },
  ];

  // ── Approve row ───────────────────────────────────────────────────────────
  const approveRow = (row, approvedAmount) => {
    const amt = approvedAmount !== undefined ? approvedAmount : row.amount;
    dispatch(commonSaveData({
      path: updateReimbursementPath(row.id),
      method: "put",
      data: { userId: user.user_id, actorName: user.full_name || user.name || user.email || "Admin", amount: String(amt), notes: row.notes || "", approveStatus: 1 },
    }));
  };

  // ── Reject row ────────────────────────────────────────────────────────────
  const rejectRow = (row) => {
    dispatch(commonSaveData({
      path: updateReimbursementPath(row.id),
      method: "put",
      data: { userId: user.user_id, actorName: user.full_name || user.name || user.email || "Admin", amount: String(row.amount), notes: row.notes || "", approveStatus: 2 },
    }));
  };

  // ── Delete row ────────────────────────────────────────────────────────────
  const deleteRow = (row) => {
    if (!window.confirm(`Delete claim #${row.id} for ₹${row.amount}?`)) return;
    dispatch(commonSaveData({
      path: deleteReimbursementPath(row.id),
      method: "delete",
      data: { userId: user.user_id, actorName: user.full_name || user.name || user.email || "Admin" },
    }));
  };

  // ── Bulk approve ──────────────────────────────────────────────────────────
  const handleBulkApprove = () => {
    const pending = displayList.filter(r => selectedRows.includes(r.id) && r.status !== "Approved");
    if (!pending.length) { toast.info("No pending claims selected."); return; }
    pending.forEach(row => dispatch(commonSaveData({
      path: updateReimbursementPath(row.id),
      method: "put",
      data: { userId: user.user_id, actorName: user.full_name || user.name || user.email || "Admin", amount: String(row.amount), notes: row.notes || "", approveStatus: 1 },
    })));
    setSelectedRows([]);
    toast.success(`Approving ${pending.length} claims…`);
  };

  // ── Add reimburse ─────────────────────────────────────────────────────────
  const handleReimburseChange = (e) => setReimburseStaff(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSaveReimburse = () => {
    if (!reimburseStaff.staffId)     { toast.error("Please select an employee");    return; }
    if (!reimburseStaff.amount)      { toast.error("Please enter an amount");        return; }
    if (!reimburseStaff.paymentDate) { toast.error("Please select an expense date"); return; }
    setIsSaving(true);
    dispatch(commonSaveData({
      path: createReimbursementPath,
      method: "post",
      data: {
        userId:        user.user_id,
        staffId:       Number(reimburseStaff.staffId),
        actorName:     user.full_name || user.name || user.email || "Admin",
        amount:        reimburseStaff.amount,
        paymentDate:   reimburseStaff.paymentDate,
        notes:         reimburseStaff.notes || "",
        expenseType:   reimburseStaff.expenseType || "Other",
        approveStatus: 0,
      },
    }));
  };

  const tabs = ["All", "Pending", "Approved", "Rejected"];

  useEffect(() => { setCurrentPage(1); }, [activeTab, filterState.search, filterState.payrollMonth, filterState.status, filterState.type, filterState.branch, filterState.shiftId]);

  if (showPolicyDashboard) {
    return (
      <PolicyDashboard
        onClose={() => {
          setShowPolicyDashboard(false);
          setPolicyDashboardEmployee(null);
        }}
        initialEmployee={policyDashboardEmployee}
      />
    );
  }

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css" />
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Outfit', sans-serif; background: var(--pay-bg); color: var(--pay-text); }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: var(--pay-bg); }
        ::-webkit-scrollbar-thumb { background: rgba(134,96,248,0.2); border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(134,96,248,0.4); }
        .glow-purple { box-shadow: 0 0 20px rgba(134,96,248,0.15); }
        .glow-green  { box-shadow: 0 0 15px rgba(34,197,94,0.2); }
        .row-hover:hover { background: rgba(22,16,50,0.5) !important; }
        @keyframes successFlash { 0%,100% { background-color: transparent; } 50% { background-color: rgba(34,197,94,0.1); } }
        .success-flash { animation: successFlash 0.8s ease-in-out; }
      `}</style>

      <div style={{ background: C.bg, minHeight: "100vh", fontFamily: "'Outfit', sans-serif", color: C.text }}>

        {adjustmentsHandoff?.adjustments?.length > 0 && (
          <div
            style={{
              margin: "20px 24px 0",
              padding: "12px 16px",
              background: "rgba(134,96,248,0.08)",
              border: "1px solid rgba(134,96,248,0.25)",
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div style={{ fontSize: 12 }}>
              <i className="fa-solid fa-paper-plane" style={{ color: C.purple, marginRight: 8 }} />
              <strong>{adjustmentsHandoff.adjustments.length}</strong> payroll adjustments received from
              Incentives & Advances for {adjustmentsHandoff.monthLabel || adjustmentsHandoff.selectedMonth}.
            </div>
            <button
              type="button"
              onClick={() => setAdjustmentsHandoff(null)}
              style={{ ...s.btnOutline, padding: "6px 12px", fontSize: 11 }}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* ── FILTER / ACTION BAR ── */}
        <section style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, margin: "20px 24px 0", padding: "10px 14px" }}>
          <div style={{ display: "flex", flexWrap: "nowrap", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            {/* Left filters */}
            <div style={{ display: "flex", flexWrap: "nowrap", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
              {/* Search */}
              <div style={{ flex: "1 1 160px", minWidth: 0, position: "relative", display: "flex", alignItems: "center" }}>
                <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: C.muted, pointerEvents: "none", zIndex: 1 }} />
                <input
                  type="text"
                  placeholder="Search employee name / ID"
                  value={filterState.search}
                  onChange={e => setFilterState(p => ({ ...p, search: e.target.value }))}
                  style={{ ...s.input, width: "100%", boxSizing: "border-box", background: C.surfaceSecondary, fontSize: 11, padding: "6px 10px 6px 28px", height: 30 }}
                />
              </div>

              {/* Month */}
              <div style={{ flex: "0 0 148px", minWidth: 0 }}>
                <MonthPicker value={filterState.payrollMonth} onChange={val => setFilterState(p => ({ ...p, payrollMonth: val }))} />
              </div>

              {/* Branch */}
              <div style={{ flex: "0 0 125px", minWidth: 0, position: "relative" }}>
                <select style={{ ...s.select, background: C.surfaceSecondary, fontSize: 11, padding: "6px 22px 6px 10px", width: "100%", boxSizing: "border-box", height: 30 }} value={filterState.branch} onChange={e => setFilterState(p => ({ ...p, branch: e.target.value }))}>
                  <option value="">All Branches</option>
                  {(branchState?.brnGetData || []).map(b => <option key={b.branchId} value={b.branchId}>{b.branch_name}</option>)}
                </select>
                <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 9, color: C.muted, pointerEvents: "none" }} />
              </div>

              {/* Shift */}
              <div style={{ flex: "0 0 115px", minWidth: 0, position: "relative" }}>
                <select style={{ ...s.select, background: C.surfaceSecondary, fontSize: 11, padding: "6px 22px 6px 10px", width: "100%", boxSizing: "border-box", height: 30 }} value={filterState.shiftId} onChange={e => setFilterState(p => ({ ...p, shiftId: e.target.value }))}>
                  <option value="">All Shifts</option>
                  {(shiftState?.shiftGetData || []).map(sh => <option key={sh.shiftId} value={sh.shiftId}>{sh.shiftName}</option>)}
                </select>
                <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 9, color: C.muted, pointerEvents: "none" }} />
              </div>

              {/* Status */}
              <div style={{ flex: "0 0 115px", minWidth: 0, position: "relative" }}>
                <select style={{ ...s.select, background: C.surfaceSecondary, fontSize: 11, padding: "6px 22px 6px 10px", width: "100%", boxSizing: "border-box", height: 30 }} value={filterState.status} onChange={e => setFilterState(p => ({ ...p, status: e.target.value }))}>
                  <option value="All">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Review">Review</option>
                </select>
                <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 9, color: C.muted, pointerEvents: "none" }} />
              </div>

              {/* Type */}
              <div style={{ flex: "0 0 110px", minWidth: 0, position: "relative" }}>
                <select style={{ ...s.select, background: C.surfaceSecondary, fontSize: 11, padding: "6px 22px 6px 10px", width: "100%", boxSizing: "border-box", height: 30 }} value={filterState.type} onChange={e => setFilterState(p => ({ ...p, type: e.target.value }))}>
                  <option value="All">All Types</option>
                  <option value="Travel">Travel</option>
                  <option value="Food">Food</option>
                  <option value="Fuel">Fuel</option>
                  <option value="Other">Other</option>
                </select>
                <i className="fa-solid fa-chevron-down" style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 9, color: C.muted, pointerEvents: "none" }} />
              </div>
            </div>

            {/* Right actions */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <button onClick={() => setShowPolicyDashboard(true)} style={{ ...s.btnPrimary, height: 30, padding: "0 12px", fontSize: 11, background: "rgba(134,96,248,0.15)", border: `1px solid rgba(134,96,248,0.5)`, boxShadow: "0 0 12px rgba(134,96,248,0.2)" }}>
                <i className="fa-solid fa-shield-halved" style={{ fontSize: 11 }} /> Policy Dashboard
              </button>
              <button style={{ ...s.btnOutline, height: 30, padding: "0 12px", fontSize: 11 }}>
                <i className="fa-solid fa-download" style={{ fontSize: 11 }} /> Export
              </button>
              <button onClick={() => setAddDialogOpen(true)} style={{ ...s.btnPrimary, height: 30, padding: "0 12px", fontSize: 11, boxShadow: "0 0 20px rgba(134,96,248,0.25)" }}>
                <i className="fa-solid fa-plus" style={{ fontSize: 11 }} /> New Claim
              </button>
            </div>
          </div>
        </section>

        {/* ── KPI CARDS ── */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, padding: "16px 24px 0" }}>
          {[
            { icon: "fa-receipt", iconColor: C.purple, iconBg: "rgba(134,96,248,0.12)", label: "Total Claims", value: summary.totalClaims, sub: `₹${Number(summary.totalAmount).toLocaleString("en-IN")} total amount` },
            { icon: "fa-clock", iconColor: "#f59e0b", iconBg: "rgba(245,158,11,0.12)", label: "Pending Approval", value: summary.pendingApprovals, sub: "Awaiting review" },
            { icon: "fa-circle-check", iconColor: "#22c55e", iconBg: "rgba(34,197,94,0.12)", label: "Approved Amount", value: `₹${Number(summary.approvedAmount).toLocaleString("en-IN")}`, sub: `${summary.rejectedCount} rejected this month` },
            { icon: "fa-shield-halved", iconColor: "#3b82f6", iconBg: "rgba(59,130,246,0.12)", label: "Total Records", value: reimbursementList.length, sub: "Claims loaded for period" },
          ].map((card, i) => (
            <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: card.iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className={`fa-solid ${card.icon}`} style={{ color: card.iconColor, fontSize: 13 }} />
                  </div>
                  <span style={{ fontSize: 11, color: C.muted, fontWeight: 500 }}>{card.label}</span>
                </div>
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>{card.value}</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 5 }}>{card.sub}</div>
            </div>
          ))}
        </section>

        {/* ── MAIN BODY ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 16, padding: "16px 24px 24px", minWidth: 0, overflow: "hidden" }}>
          {/* LEFT — claims table */}
          <div style={{ minWidth: 0, overflow: "hidden" }}>
            {/* Bulk action bar */}
            {selectedCount > 0 && (
              <div style={{ background: "rgba(134,96,248,0.1)", border: `1px solid rgba(134,96,248,0.3)`, borderRadius: 14, padding: "12px 16px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 0 20px rgba(134,96,248,0.1)" }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{selectedCount} Claims Selected</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button onClick={() => setBulkModal(true)} style={{ ...s.btnPrimary, fontSize: 12, padding: "6px 14px" }}>
                    <i className="fa-solid fa-check" style={{ fontSize: 11 }} /> Approve Selected
                  </button>
                  <button onClick={() => { selectedRows.forEach(id => { const r = displayList.find(x => x.id === id); if (r) rejectRow(r); }); setSelectedRows([]); }} style={{ padding: "6px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, fontSize: 12, fontWeight: 500, color: "#ef4444", cursor: "pointer" }}>
                    <i className="fa-solid fa-xmark" style={{ fontSize: 11, marginRight: 4 }} /> Reject Selected
                  </button>
                  <button style={{ padding: "6px 14px", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 8, fontSize: 12, fontWeight: 500, color: "#f59e0b", cursor: "pointer" }}>
                    <i className="fa-solid fa-pause" style={{ fontSize: 11, marginRight: 4 }} /> Hold Selected
                  </button>
                  <button style={{ ...s.btnOutline, fontSize: 12, padding: "6px 14px" }}>
                    <i className="fa-solid fa-download" style={{ fontSize: 12}} /> Export Selected
                  </button>
                  <button onClick={() => setSelectedRows([])} style={{ ...s.btnOutline, fontSize: 12, padding: "6px 14px", color: C.muted }}>
                    Clear Selection
                  </button>
                </div>
              </div>
            )}

            {/* Table card */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", minWidth: 0 }}>
              <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}` }}>
                <h2 style={{ fontSize: 12, fontWeight: 600, margin: "0 0 14px" }}>Reimbursement Claims</h2>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {tabs.map(t => (
                    <button key={t} onClick={() => setActiveTab(t)} style={s.tabBtn(activeTab === t)}>{t}</button>
                  ))}
                </div>
              </div>

              <div style={{ overflowX: "auto" }}>
                {/* CSS Grid Table Header */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "40px minmax(160px, 1.2fr) minmax(90px, 0.7fr) minmax(100px, 0.7fr) minmax(80px, 0.6fr) minmax(100px, 0.8fr) minmax(90px, 0.7fr) minmax(90px, 0.7fr) minmax(130px, 1fr)",
                  background: "rgba(22,16,50,0.5)",
                  borderBottom: `1px solid ${C.border}`,
                  padding: "8px 12px",
                  fontSize: 10,
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: C.muted,
                  position: "sticky",
                  top: 0,
                  zIndex: 10
                }}>
                  <div style={{ textAlign: "center" }}>
                    <label style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                      <input type="checkbox" checked={isAllSelected} onChange={toggleSelectAll} style={{ position: "absolute", opacity: 0, width: 0, height: 0 }} />
                      <span style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        width: 15, height: 15, borderRadius: 4,
                        border: isAllSelected ? "2px solid #8660F8" : "2px solid rgba(134,96,248,0.4)",
                        background: isAllSelected ? "#8660F8" : "rgba(134,96,248,0.06)",
                        transition: "all 0.15s ease", flexShrink: 0,
                      }}>
                        {isAllSelected && (
                          <svg width="8" height="6" viewBox="0 0 9 7" fill="none">
                            <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                        {!isAllSelected && selectedRows.length > 0 && (
                          <svg width="7" height="2" viewBox="0 0 8 2" fill="none">
                            <rect x="0" y="0.5" width="8" height="1" rx="0.5" fill="#8660F8"/>
                          </svg>
                        )}
                      </span>
                    </label>
                  </div>
                  <div>Employee</div>
                  <div>Dept</div>
                  <div>Category Type</div>
                  <div>Amount</div>
                  <div>Policy Check</div>
                  <div>Submitted</div>
                  <div>Status</div>
                  <div style={{ textAlign: "right" }}>Actions</div>
                </div>

                {/* Grid Body */}
                <div style={{ minHeight: 400 }}>
                  {displayList.length === 0 ? (
                    <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>
                      No claims found
                    </div>
                  ) : displayList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((row, i) => {
                    const isSelected = selectedRows.includes(row.id);
                    const rowStatus  = row.status || "Pending";
                    const amount     = Number(row.amount || 0);
                    const isPending  = rowStatus === "Pending" || rowStatus === "Review";

                    return (
                      <div
                        key={i}
                        className="row-hover"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "40px minmax(160px, 1.2fr) minmax(90px, 0.7fr) minmax(100px, 0.7fr) minmax(80px, 0.6fr) minmax(100px, 0.8fr) minmax(90px, 0.7fr) minmax(90px, 0.7fr) minmax(130px, 1fr)",
                          padding: "8px 12px",
                          borderBottom: `1px solid ${C.border}`,
                          background: isSelected ? "rgba(134,96,248,0.06)" : "transparent",
                          transition: "background 0.15s",
                          alignItems: "center"
                        }}
                      >
                        {/* Checkbox */}
                        <div style={{ textAlign: "center" }}>
                          <label style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                            <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(row.id)} style={{ position: "absolute", opacity: 0, width: 0, height: 0 }} />
                            <span style={{
                              display: "flex", alignItems: "center", justifyContent: "center",
                              width: 15, height: 15, borderRadius: 4,
                              border: isSelected ? "2px solid #8660F8" : "2px solid rgba(134,96,248,0.35)",
                              background: isSelected ? "#8660F8" : "rgba(134,96,248,0.05)",
                              transition: "all 0.15s ease", flexShrink: 0,
                            }}>
                              {isSelected && (
                                <svg width="8" height="6" viewBox="0 0 9 7" fill="none">
                                  <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </span>
                          </label>
                        </div>

                        {/* Employee */}
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.purple, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                              {getInitials?.(row.staffName) || "?"}
                            </div>
                            <div>
                              <div style={{ fontWeight: 500, fontSize: 11 }}>{row.staffName || "—"}</div>
                              <div style={{ fontSize: 9, color: C.muted }}>{row.employeeId}</div>
                            </div>
                          </div>
                        </div>

                        {/* Department */}
                        <div style={{ fontSize: 11, color: C.muted }}>{row.department || "—"}</div>

                        {/* Category type */}
                        <div>
                          <ExpenseTypeCell type={row.category || row.expenseType || "Other"} />
                        </div>

                        {/* Amount */}
                        <div style={{ fontWeight: 600, fontSize: 12 }}>
                          ₹{amount.toLocaleString("en-IN")}
                        </div>

                        {/* Policy check */}
                        <div>
                          <PolicyBadge
                            check={row.policyCheck}
                            label={row.policyLabel}
                            onClick={() => {
                              setPolicyDashboardEmployee(row);
                              setShowPolicyDashboard(true);
                            }}
                          />
                        </div>

                        {/* Submitted date */}
                        <div style={{ color: C.muted, fontSize: 10, whiteSpace: "nowrap" }}>
                          {row.submittedDate || row.paymentDate || "—"}
                        </div>

                        {/* Status */}
                        <div>
                          <StatusBadge status={rowStatus} />
                        </div>

                        {/* Actions */}
                        <div style={{ textAlign: "right" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                            <button onClick={() => navigate('/claim-details', { state: { claim: row } })} style={{ padding: "3px 8px", fontSize: 10, fontWeight: 500, color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, background: "transparent", cursor: "pointer" }}>
                              View
                            </button>
                            {isPending && (
                              <>
                                <button onClick={(e) => { const rect = e.currentTarget.getBoundingClientRect(); setPopover({ open: true, amount, anchorRect: rect, rowId: row.id, rowData: row }); }} style={{ padding: "3px 8px", fontSize: 10, fontWeight: 500, color: "#fff", background: "rgba(34,197,94,0.9)", border: "none", borderRadius: 6, cursor: "pointer" }}>
                                  Approve
                                </button>
                                <button onClick={() => rejectRow(row)} style={{ padding: "3px 8px", fontSize: 10, fontWeight: 500, color: "#ef4444", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, background: "transparent", cursor: "pointer" }}>
                                  Reject
                                </button>
                                <button onClick={() => deleteRow(row)} style={{ padding: "3px 8px", fontSize: 10, fontWeight: 500, color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, background: "transparent", cursor: "pointer" }}>
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination row */}
                <div style={{ padding: "8px 12px", borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 11, color: C.muted }}>
                    Showing {Math.min((currentPage - 1) * PAGE_SIZE + 1, displayList.length)}–{Math.min(currentPage * PAGE_SIZE, displayList.length)} of {displayList.length} claims
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surfaceSecondary, color: currentPage === 1 ? C.border : C.muted, cursor: currentPage === 1 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <i className="fa-solid fa-chevron-left" style={{ fontSize: 10 }} />
                    </button>
                    {(() => {
                      const totalPages = Math.max(1, Math.ceil(displayList.length / PAGE_SIZE));
                      const pages = [];
                      let start = Math.max(1, currentPage - 1);
                      let end   = Math.min(totalPages, start + 2);
                      if (end - start < 2) start = Math.max(1, end - 2);
                      for (let p = start; p <= end; p++) pages.push(p);
                      return pages.map(p => (
                        <button key={p} onClick={() => setCurrentPage(p)} style={{ width: 28, height: 28, borderRadius: 6, border: p === currentPage ? "none" : `1px solid ${C.border}`, background: p === currentPage ? C.purple : C.surfaceSecondary, color: p === currentPage ? "#fff" : C.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: p === currentPage ? 600 : 400 }}>
                          {p}
                        </button>
                      ));
                    })()}
                    <button disabled={currentPage >= Math.ceil(displayList.length / PAGE_SIZE)} onClick={() => setCurrentPage(p => p + 1)} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surfaceSecondary, color: currentPage >= Math.ceil(displayList.length / PAGE_SIZE) ? C.border : C.muted, cursor: currentPage >= Math.ceil(displayList.length / PAGE_SIZE) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <i className="fa-solid fa-chevron-right" style={{ fontSize: 10 }} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ── RECENT ACTIVITY — below table ── */}
            <section style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginTop: 16 }}>
              <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Recent Activity</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {activityLoading && <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>Loading activity...</p>}
                {!activityLoading && recentActivity.length === 0 && <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>No recent activity found.</p>}
                {!activityLoading && recentActivity.map((item) => {
                  const amountText = Number(item.amount || 0).toLocaleString("en-IN");
                  return (
                    <div key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: item.iconBg || `rgba(134,96,248,0.1)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                        <i className={`fa-solid ${item.icon || "fa-file-invoice"}`} style={{ color: item.iconColor || C.purple, fontSize: 12 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 13, margin: 0 }}>
                          <strong>{item.actorName || "System"}</strong>{" "}
                          {item.message || "updated reimbursement claim"}{" "}
                          {item.employeeName ? <>for <strong>{item.employeeName}</strong> </> : null}
                          <strong style={{ color: C.purple }}>Rs {amountText}</strong>
                        </p>
                        <p style={{ fontSize: 11, color: C.muted, margin: "4px 0 0" }}>{item.timeAgo || item.formattedDate || ""}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          {/* RIGHT — insight cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0, overflow: "hidden" }}>
            {/* Today Pending Actions */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>Today Pending Actions</h3>
              {[
                { label: "Pending Claims", value: pendingCountLive, color: C.text },
                { label: "Selected", value: selectedCount, color: C.purple },
                { label: "Ready to Approve", value: readyToApproveLive, color: "#22c55e" },
                { label: "Need Review", value: needReviewLive, color: "#f59e0b" },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: i < 3 ? `1px solid ${C.border}` : "none" }}>
                  <span style={{ fontSize: 12, color: C.muted }}>{item.label}</span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: item.color }}>{item.value}</span>
                </div>
              ))}
            </div>

            {/* Policy Matrix */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>Reimbursement Policy Matrix</h3>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}`, marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: C.muted }}>Designation</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>Monthly Cap</span>
              </div>
              {policyMatrixRoles.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0" }}>
                  <span style={{ fontSize: 12, color: C.text }}>{r.role || r.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.purple }}>{r.cap || r.capAmount || "—"}</span>
                </div>
              ))}
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>Category Limits</div>
                {["Travel", "Food", "Fuel", "Other"].map((label, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <i className={`fa-solid ${label === "Travel" ? "fa-plane" : label === "Food" ? "fa-utensils" : label === "Fuel" ? "fa-gas-pump" : "fa-briefcase"}`} style={{ color: label === "Travel" ? C.purple : label === "Food" ? "#22c55e" : label === "Fuel" ? "#3b82f6" : "#a855f7", fontSize: 12 }} />
                      <span style={{ fontSize: 12 }}>{label}</span>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>{categoryLimitsLive[label] || "—"}</span>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 4 }}>
                <p style={{ fontSize: 10, color: C.muted, textAlign: "center", margin: 0 }}>Limits auto-check during claim submission</p>
              </div>
            </div>

            {/* Policy Alerts */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Policy Alerts</h3>
              {[
                { icon: "fa-triangle-exclamation", color: "#f59e0b", bg: "rgba(245,158,11,0.05)", border: "rgba(245,158,11,0.2)", label: "Near monthly limit", count: policyAlertsLive.nearMonthlyLimit ?? 0 },
                { icon: "fa-circle-exclamation", color: "#ef4444", bg: "rgba(239,68,68,0.05)", border: "rgba(239,68,68,0.2)", label: "Exceeded category cap", count: policyAlertsLive.exceededCategoryCap ?? 0 },
                { icon: "fa-shield-halved", color: "#3b82f6", bg: "rgba(59,130,246,0.05)", border: "rgba(59,130,246,0.2)", label: "Approval exceptions", count: policyAlertsLive.approvalExceptions ?? 0 },
              ].map((a, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: a.bg, border: `1px solid ${a.border}`, borderRadius: 8, marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <i className={`fa-solid ${a.icon}`} style={{ color: a.color, fontSize: 12 }} />
                    <span style={{ fontSize: 12, color: C.text }}>{a.label}</span>
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: a.color }}>{a.count}</span>
                </div>
              ))}
            </div>

            {/* Category Breakdown */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Category Breakdown</h3>
              {categoryBreakdownLive.map((c, i) => (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: C.muted }}>{c.label}</span>
                    <span style={{ fontWeight: 600 }}>{c.amount}</span>
                  </div>
                  <div style={{ width: "100%", background: C.surfaceSecondary, height: 6, borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ width: `${c.pct}%`, height: "100%", borderRadius: 3, background: c.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length] }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Approval Alerts */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Approval Alerts</h3>
              {approvalAlertsLive.map((a, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: a.bg, border: `1px solid ${a.border}`, borderRadius: 8, marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <i className={`fa-solid ${a.icon}`} style={{ color: a.color, fontSize: 12 }} />
                    <span style={{ fontSize: 12, color: C.text }}>{a.label}</span>
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: a.color }}>{a.count}</span>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Quick Actions</h3>
              {[
                { icon: "fa-pen-to-square", label: "Edit Limits", onClick: () => setPolicyDrawer({ open: true, employee: null }) },
                { icon: "fa-shield-halved", label: "Assign Policy", onClick: () => {} },
                { icon: "fa-arrows-rotate", label: "Bulk Update Caps", onClick: () => {} },
                { icon: "fa-download", label: "Download Policy Report", onClick: () => {} },
              ].map((a, i) => (
                <button key={i} onClick={a.onClick} style={{ ...s.btnOutline, width: "100%", marginBottom: 8, justifyContent: "flex-start" }}>
                  <i className={`fa-solid ${a.icon}`} style={{ fontSize: 12 }} /> {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── OVERLAYS ── */}
      <ApprovalPopover
        open={popover.open}
        anchorRect={popover.anchorRect}
        amount={popover.amount}
        onConfirm={(approvedAmt) => { approveRow(popover.rowData, approvedAmt); setPopover(p => ({ ...p, open: false })); }}
        onClose={() => setPopover(p => ({ ...p, open: false }))}
      />

      <BulkModal
        open={bulkModal}
        count={selectedCount}
        totalAmount={selectedTotal}
        onConfirm={handleBulkApprove}
        onClose={() => setBulkModal(false)}
      />

      <PolicyLimitsDrawer
        open={policyDrawer.open}
        employee={policyDrawer.employee}
        onClose={() => setPolicyDrawer({ open: false, employee: null })}
      />

      <AddReimburseDialog
        open={addDialogOpen}
        employees={employees || []}
        reimburseStaff={reimburseStaff}
        onChange={handleReimburseChange}
        onSave={handleSaveReimburse}
        onClose={() => setAddDialogOpen(false)}
        isSaving={isSaving}
      />

      <ToastContainer position="top-right" autoClose={2000} theme="dark" />
    </>
  );
};

export default Reimbursement;